import { z } from 'zod';

export const validators = {
    activateAccountV2Request: {
        userId: z.string().min(1),
        activateCode: z.string().min(1).max(1024),
        password: z
            .string()
            .min(10)
            .max(256)
            .regex(/^.{10,256}$/),
    },
    activateElectricalCheckListCommand: {
        electricalCheckListId: z.string().min(1),
    },
    activateElectricalInspectionNormListCommand: {
        electricalInspectionNormListId: z.string().min(1),
    },
    addRoleToUserCommand: {
        userId: z.string().min(1),
    },
    changeAccountEmailV2Request: {
        email: z.union([z.literal(''), z.string().email()]),
        password: z.string().min(1).max(256),
    },
    changeAccountPasswordV2Request: {
        currentPassword: z.string().min(1).max(256),
        newPassword: z
            .string()
            .min(10)
            .max(256)
            .regex(/^.{10,256}$/),
    },
    changeElectricalProjectStateCommand: {
        electricalProjectVersionId: z.string().min(1),
        status: z.enum([
            'Request',
            'PriceQuote',
            'Planned',
            'Inspection',
            'RepeatInspection',
            'Complete',
            'Failed',
            'Order',
        ]),
    },
    changeUserEmailCommand: {
        userId: z.string().min(1),
        email: z.union([z.literal(''), z.string().email()]),
    },
    copyDocumentCommand: {
        documentId: z.string().min(1),
    },
    copyElectricalProjectCommand: {
        electricalProjectId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        description: z.string().max(100).nullable().optional(),
    },
    copyElectricalProjectDistributionBoardDeviceCommand: {
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        count: z.number().min(1).max(200),
    },
    createAppCommandLogCommand: {
        appKind: z.enum(['ElectraApp', 'PortalApp']).nullable().optional(),
        appUrl: z.string().max(2048).nullable().optional(),
        appVersion: z.string().max(100).nullable().optional(),
        apiUrl: z.string().max(2048).nullable().optional(),
        apiHttpMethod: z
            .enum(['Get', 'Head', 'Post', 'Put', 'Patch', 'Delete', 'Options'])
            .nullable()
            .optional(),
        apiRequestId: z.string().nullable().optional(),
        date: z.date().nullable().optional(),
        commandName: z.string().max(128).nullable().optional(),
        path: z.string().max(2048).nullable().optional(),
        queryString: z.string().max(2048).nullable().optional(),
        body: z.string().max(1000000).nullable().optional(),
        fileName: z.string().max(256).nullable().optional(),
        fileContentType: z.string().max(128).nullable().optional(),
        fileSize: z.number().nullable().optional(),
        deviceId: z.string().max(200).nullable().optional(),
        deviceName: z.string().max(200).nullable().optional(),
        deviceNetwork: z.string().max(200).nullable().optional(),
        message: z.string().max(16000).nullable().optional(),
        exception: z.string().max(16000).nullable().optional(),
        createAppCommandLogCommandRecord: {
            recordIdField: z.string().nullable().optional(),
            recordId: z.string().nullable().optional(),
        },
    },
    createAppLogCommand: {
        appKind: z.enum(['ElectraApp', 'PortalApp']).nullable().optional(),
        appUrl: z.string().max(2048).nullable().optional(),
        appVersion: z.string().max(100).nullable().optional(),
        apiUrl: z.string().max(2048).nullable().optional(),
        apiHttpMethod: z
            .enum(['Get', 'Head', 'Post', 'Put', 'Patch', 'Delete', 'Options'])
            .nullable()
            .optional(),
        apiRequestId: z.string().nullable().optional(),
        logLevel: z
            .enum(['Fatal', 'Error', 'Warn', 'Info', 'Debug'])
            .nullable()
            .optional(),
        date: z.date().nullable().optional(),
        deviceId: z.string().max(200).nullable().optional(),
        deviceName: z.string().max(200).nullable().optional(),
        deviceNetwork: z.string().max(200).nullable().optional(),
        message: z.string().max(16000).nullable().optional(),
        exception: z.string().max(16000).nullable().optional(),
    },
    createContactPersonCommand: {
        organizationId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
    },
    createDefaultElectricalCheckListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
    },
    createDefaultElectricalInspectionNormListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
    },
    createDefaultElectricalObservationStandardTextItemsCommand: {
        organizationId: z.string().min(1),
    },
    createDocumentTemplateCommand: {
        organizationId: z.string().min(1),
        pdfKind: z.enum([
            'ElectricalProjectPriceQuote',
            'ElectricalProjectInspectionPlan',
            'ElectricalProjectScope8Report',
            'ElectricalProjectScope10Report',
            'ElectricalProjectScope12Report',
            'ElectricalProjectNen1010Report',
            'ElectricalProjectNen3140Report',
            'ElectricalProjectPowerShutdownPlan',
            'ElectricalProjectSeriousDefectReport',
            'ElectricalProjectObservationReport',
        ]),
        intervalKind: z.enum(['None', 'First', 'Repeat']),
        name: z.string().min(1).max(100),
        description: z.string().min(1).max(500),
        groupName: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
    },
    createElectricalCheckListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
    },
    createElectricalCheckListItemCommand: {
        electricalCheckListId: z.string().min(1),
        kind: z.enum([
            'DistributionBoard',
            'FinalCircuit',
            'PhotovoltaicInverter',
            'Machine',
            'Other',
            'Scope12InspectionPlanDocument',
            'Scope12VisualInspection',
            'Scope12AdditionalInspectionPoints',
            'Scope12DocumentationDCSide',
            'Scope12ManualAndDataSpecifications',
            'Scope12GeneralRemarks',
            'Scope12PVInstallationPowerLine',
            'Scope12InspectionExecution',
            'Scope12DocumentationACSide',
            'Scope12MeasurementsAndTesting',
            'Scope12VisualInspectionOtherParts',
            'Scope12RoofConstructionCalculation',
            'InspectionPlanSafetyGuidelinesObjectLocation',
            'InspectionPlanInstallationSchematicsAndDocumentation',
            'InspectionPlanTestsAndMeasurements',
        ]),
        enableNotApplicable: z.boolean().nullable().optional(),
        enableObservations: z.boolean().nullable().optional(),
        description: z.string().min(1).max(1000),
        positiveLabelText: z.string().max(20).nullable().optional(),
        negativeLabelText: z.string().max(20).nullable().optional(),
        notApplicableLabelText: z.string().max(20).nullable().optional(),
        order: z.number().nullable().optional(),
    },
    createElectricalCheckListTextCommand: {
        electricalCheckListItemId: z.string().min(1),
        kind: z.enum(['Remark', 'Recommendation']),
        description: z.string().min(1).max(1000),
        order: z.number().nullable().optional(),
        createElectricalCheckListTextCommandReport: {
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        },
    },
    createElectricalInspectionNormItemCommand: {
        electricalInspectionNormListId: z.string().min(1),
        normKind: z.enum(['NEN', 'SCIOS', 'NPR', 'NTA', 'Other']),
        inspectionKinds: z
            .array(
                z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
            )
            .nullable()
            .optional(),
        name: z.string().max(1000).nullable().optional(),
        description: z.string().max(1000).nullable().optional(),
        order: z.number().nullable().optional(),
        isCustomInput: z.boolean(),
    },
    createElectricalInspectionNormListCommand: {
        organizationId: z.string().min(1),
        description: z.string().min(1).max(100),
    },
    createElectricalMeasurementDeviceCalibrationCommand: {
        electricalMeasurementDeviceId: z.string().min(1),
        certificateNumber: z.string().min(1).max(500),
        startDate: z.date(),
        endDate: z.date(),
    },
    createElectricalMeasurementDeviceCommand: {
        organizationId: z.string().min(1),
        userId: z.string().nullable().optional(),
        name: z.string().min(1).max(300),
        brandName: z.string().min(1).max(100),
        deviceType: z.string().min(1).max(100),
        serialNumber: z.string().min(1).max(100),
        calibrationKind: z.enum(['Required', 'Optional', 'NotApplicable']),
        inspectionKinds: z
            .array(
                z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
            )
            .nullable()
            .optional(),
    },
    createElectricalObservationStandardTextItemCommand: {
        organizationId: z.string().min(1),
        objectKind: z.enum([
            'DistributionBoard',
            'FinalCircuit',
            'DistributionBoardFile',
            'DistributionBoardDevice',
            'RisoMeasurement',
            'PVInverter',
            'PVString',
        ]),
        textKind: z.enum(['Remark', 'Recommendation']),
        description: z.string().min(1).max(1000),
        order: z.number().nullable().optional(),
        createElectricalObservationStandardTextItemCommandReport: {
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        },
    },
    createElectricalProjectCheckListItemCommand: {
        electricalProjectCheckListItemId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        electricalCheckListItemId: z.string().min(1),
        objectKind: z.enum(['DistributionBoard', 'FinalCircuit', 'Project']),
        objectId: z.string().min(1),
    },
    createElectricalProjectCommand: {
        organizationId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        description: z.string().max(100).nullable().optional(),
        isCombinationInspection: z
            .enum(['False', 'True'])
            .nullable()
            .optional(),
    },
    createElectricalProjectContactPersonCommand: {
        electricalProjectContactPersonId: z.string().nullable().optional(),
        electricalProjectOrganizationId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        receivePriceQuote: z.boolean().nullable().optional(),
        receiveInvoice: z.boolean().nullable().optional(),
        receiveInspectionPlan: z.boolean().nullable().optional(),
        receiveInspectionReport: z.boolean().nullable().optional(),
        receivePowerShutdownPlan: z.boolean().nullable().optional(),
        isResponsibleForPowerSystem: z.boolean().nullable().optional(),
    },
    createElectricalProjectDistributionBoardCommand: {
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        kind: z
            .enum([
                'MainDistributionBoard',
                'SubDistributionBoard',
                'ControlBox',
            ])
            .nullable()
            .optional(),
        isPVInstallation: z.boolean().nullable().optional(),
        order: z.number().nullable().optional(),
        identifier: z.string().max(100).nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCurrentMeasurements: z.boolean().nullable().optional(),
        reportVoltageMeasurements: z.boolean().nullable().optional(),
        reportGroundImpedanceMeasurements: z.boolean().nullable().optional(),
        reportNeutralImpedanceMeasurements: z.boolean().nullable().optional(),
        reportResidualCurrentDevices: z.boolean().nullable().optional(),
        reportResidualCurrentCircuitBreakerDevices: z
            .boolean()
            .nullable()
            .optional(),
        reportFinalCircuitProtectiveDevices: z.boolean().nullable().optional(),
        reportIsolationResistanceMeasurements: z
            .boolean()
            .nullable()
            .optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        skipCreateCheckListItems: z.boolean().nullable().optional(),
    },
    createElectricalProjectDistributionBoardDeviceCommand: {
        electricalProjectDistributionBoardDeviceId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectDistributionBoardId: z.string().min(1),
        deviceKind: z.enum([
            'ResidualCurrentDevice',
            'ResidualCurrentCircuitBreakerDevice',
            'FinalCircuitProtectiveDevice',
            'IsolationResistanceMeasurement',
        ]),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        residualCurrentDeviceType: z.string().max(50).nullable().optional(),
        ratedCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        tripDelay: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ratedResidualCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        fuseKind: z.string().max(50).nullable().optional(),
        trippingCharacteristic: z.string().max(50).nullable().optional(),
        breakerRatedCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isTestButtonFunctional: z.boolean().nullable().optional(),
        resistanceL1PE: z.number().nullable().optional(),
        resistanceL2PE: z.number().nullable().optional(),
        resistanceL3PE: z.number().nullable().optional(),
        resistanceNeutralPE: z.number().nullable().optional(),
        resistanceL1Neutral: z.number().nullable().optional(),
        resistanceL2Neutral: z.number().nullable().optional(),
        resistanceL3Neutral: z.number().nullable().optional(),
        measureResistanceBetweenPowerPhases: z.boolean().nullable().optional(),
        resistanceL1L2: z.number().nullable().optional(),
        resistanceL1L3: z.number().nullable().optional(),
        resistanceL2L3: z.number().nullable().optional(),
        risoSign: z.enum(['<', '>', '=']).nullable().optional(),
        risoValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportRisoMeasurements: z.boolean().nullable().optional(),
    },
    createElectricalProjectDistributionBoardFileCommand: {
        electricalProjectDistributionBoardFileId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectDistributionBoardId: z.string().min(1),
        kind: z.enum([
            'EnclosureOpenImage',
            'EnclosureClosedImage',
            'ExtraImage',
            'ThermalImage',
            'Measurements',
        ]),
        electricalProjectFileId: z.string().nullable().optional(),
        order: z.number().nullable().optional(),
        remark: z.string().max(100).nullable().optional(),
    },
    createElectricalProjectDocumentCommand: {
        electricalProjectVersionId: z.string().min(1),
        kind: z
            .enum([
                'PriceQuote',
                'InspectionPlan',
                'PowerShutdownPlan',
                'Reports',
                'ObservationReport',
            ])
            .nullable()
            .optional(),
        kinds: z
            .array(
                z.enum([
                    'PriceQuote',
                    'InspectionPlan',
                    'PowerShutdownPlan',
                    'Reports',
                    'ObservationReport',
                ])
            )
            .nullable()
            .optional(),
    },
    createElectricalProjectEquipotentialBondingMeasurementCommand: {
        electricalProjectEquipotentialBondingMeasurementId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectVersionId: z.string().min(1),
        location: z.string().max(100).nullable().optional(),
        resistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        order: z.number().nullable().optional(),
    },
    createElectricalProjectFileCommand: {
        electricalProjectFileId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        kind: z.enum([
            'ObjectImage',
            'DistributionBoard',
            'FinalCircuit',
            'Observation',
            'TechnicalDrawing',
            'Floorplan',
            'PVInstallation',
            'PVInverter',
            'PriceQuote',
            'InspectionPlan',
            'ObservationLog',
            'Measurements',
        ]),
        order: z.number().nullable().optional(),
        description: z.string().max(200).nullable().optional(),
    },
    createElectricalProjectFinalCircuitCommand: {
        electricalProjectFinalCircuitId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        kind: z
            .enum([
                'FinalCircuit',
                'PhotovoltaicInverter',
                'Machine',
                'Space',
                'Building',
                'Other',
            ])
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
        skipCreateCheckListItems: z.boolean().nullable().optional(),
    },
    createElectricalProjectFinalCircuitFileCommand: {
        electricalProjectFinalCircuitFileId: z.string().nullable().optional(),
        electricalProjectFinalCircuitId: z.string().min(1),
        kind: z.enum([
            'DetailsImage',
            'OverviewImage',
            'ExtraImage',
            'ThermalImage',
        ]),
        electricalProjectFileId: z.string().min(1),
        order: z.number().nullable().optional(),
    },
    createElectricalProjectInspectionDateCommand: {
        electricalProjectInspectionDateId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        date: z.date(),
    },
    createElectricalProjectInspectionPlanRemarkCommand: {
        electricalProjectInspectionPlanRemarkId: z
            .string()
            .nullable()
            .optional(),
        electricalProjectVersionId: z.string().min(1),
        order: z.number().nullable().optional(),
        subject: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
    },
    createElectricalProjectLinksCommand: {
        electricalProjectVersionId: z.string().min(1),
    },
    createElectricalProjectLvbagObjectCommand: {
        electricalProjectVersionId: z.string().min(1),
        objectKind: z.enum(['Address', 'Building']),
        bAGBuildingIdentifier: z
            .string()
            .min(1)
            .regex(/^[0-9]{4}10[0-9]{10}$/),
        bAGAddressIdentifier: z
            .string()
            .regex(/^[0-9]{4}20[0-9]{10}$/)
            .nullable()
            .optional(),
    },
    createElectricalProjectMeasurementDeviceCommand: {
        electricalProjectVersionId: z.string().min(1),
        electricalMeasurementDeviceId: z.string().min(1),
        inspectionKinds: z
            .array(
                z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
            )
            .nullable()
            .optional(),
    },
    createElectricalProjectObservationCommand: {
        electricalProjectObservationId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        objectKind: z.enum([
            'DistributionBoard',
            'FinalCircuit',
            'DistributionBoardFile',
            'DistributionBoardDevice',
            'CheckListItem',
            'RisoMeasurement',
            'PVInverter',
            'PVString',
            'DistributionBoardInformation',
            'DistributionBoardMeasurements',
            'DistributionBoardImages',
            'DistributionBoardThermalImages',
            'DistributionBoardGeneralInformation',
            'DistributionBoardCurrent',
            'DistributionBoardVoltage',
            'DistributionBoardCircuitImpedance',
            'DistributionBoardNetImpedance',
            'FinalCircuitImages',
            'FinalCircuitThermalImages',
        ]),
        objectId: z.string().min(1),
        order: z.number().nullable().optional(),
        floorplanElectricalProjectFileId: z.string().nullable().optional(),
        floorplanPinCoordinateX: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        floorplanPinCoordinateY: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        locationRemark: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
        createElectricalProjectObservationCommandReport: {
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            enabled: z.boolean(),
            remarkKind: z.enum(['None', 'Same', 'Custom']),
            remark: z.string().max(1000).nullable().optional(),
            recommendationKind: z.enum(['None', 'Same', 'Custom']),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        },
    },
    createElectricalProjectObservationFileCommand: {
        electricalProjectObservationId: z.string().min(1),
        electricalProjectFileId: z.string().min(1),
        order: z.number().nullable().optional(),
    },
    createElectricalProjectObservationLogCommand: {
        electricalProjectObservationId: z.string().min(1),
        status: z
            .enum(['SolutionRejected', 'SolutionApproved'])
            .nullable()
            .optional(),
        message: z.string().max(5000).nullable().optional(),
        createElectricalProjectObservationLogPortalCommandFile: {
            electricalProjectFileId: z.string().min(1),
        },
    },
    createElectricalProjectObservationLogPortalCommand: {
        electricalProjectObservationId: z.string().min(1),
        createElectricalProjectObservationLogPortalCommandUser: {
            name: z.string().min(1).max(100),
            email: z.string().min(1).max(256),
            organizationName: z.string().min(1).max(100),
        },
        status: z
            .enum([
                'IssueReported',
                'WontSolveIssue',
                'ToSolveIssue',
                'IssueSolved',
            ])
            .nullable()
            .optional(),
        message: z.string().max(5000).nullable().optional(),
        createElectricalProjectObservationLogPortalCommandFile: {
            electricalProjectFileId: z.string().min(1),
        },
    },
    createElectricalProjectOrganizationCommand: {
        electricalProjectOrganizationId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        legalPersonKind: z.enum(['NaturalPerson', 'JuridicalPerson']),
        createElectricalProjectOrganizationCommandRegistration: {
            sourceKind: z.enum(['NLKVK']).nullable().optional(),
            createElectricalProjectOrganizationCommandNLKvk: {
                kVKNumber: z
                    .string()
                    .min(1)
                    .regex(/^[0-9]{8}$/),
                branchNumber: z
                    .string()
                    .regex(/^[0-9]{12}$/)
                    .nullable()
                    .optional(),
            },
        },
        createElectricalProjectOrganizationCommandAddress: {
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            administrativeArea: z.string().max(64).nullable().optional(),
            countryId: z.string().nullable().optional(),
        },
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        isProjectCustomer: z.boolean(),
        isObjectOwner: z.boolean(),
        isObjectAdministrator: z.boolean(),
        isObjectTenant: z.boolean(),
        isPVInstallationConstructionCompany: z.boolean().nullable().optional(),
    },
    createElectricalProjectPVInverterCommand: {
        electricalProjectPVInverterId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        brandModel: z.string().max(100).nullable().optional(),
        serialNumber: z.string().max(100).nullable().optional(),
        firmwareVersion: z.string().max(100).nullable().optional(),
        ratedPower: z.number().min(0).max(100000).nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        order: z.number().nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportImpedanceMeasurements: z.boolean().nullable().optional(),
        reportPVStrings: z.boolean().nullable().optional(),
    },
    createElectricalProjectPVInverterFileCommand: {
        electricalProjectPVInverterFileId: z.string().nullable().optional(),
        electricalProjectPVInverterId: z.string().min(1),
        electricalProjectFileId: z.string().min(1),
        kind: z.enum([
            'PVInverterImage',
            'PVStringTable',
            'PVInverterThermalImage',
        ]),
        order: z.number().nullable().optional(),
    },
    createElectricalProjectPVStringCommand: {
        electricalProjectPVStringId: z.string().nullable().optional(),
        electricalProjectPVInverterId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        voltage: z.number().min(0).max(100000).nullable().optional(),
        resistance: z.number().min(0).max(100000).nullable().optional(),
        current: z.number().min(0).max(100000).nullable().optional(),
        order: z.number().nullable().optional(),
    },
    createElectricalProjectRisoMeasurementCommand: {
        electricalProjectRisoMeasurementId: z.string().nullable().optional(),
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        circuitKind: z
            .enum([
                'L-PE / N-PE',
                'L1-L2',
                'L2-L3',
                'L1-L3',
                'L1-N',
                'L2-N',
                'L3-N',
                'L1-PE',
                'L2-PE',
                'L3-PE',
                'N-PE',
                'L-PE',
            ])
            .nullable()
            .optional(),
        measuredValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        minimumValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isFaulty: z.boolean().nullable().optional(),
        order: z.number().nullable().optional(),
    },
    createElectricalProjectTechnicalDrawingCommand: {
        electricalProjectTechnicalDrawingId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        isDrawingPresent: z.boolean().nullable().optional(),
        electricalProjectFileId: z.string().nullable().optional(),
        name: z.string().min(1).max(200),
        location: z.string().max(200).nullable().optional(),
        description: z.string().max(500).nullable().optional(),
        order: z.number().nullable().optional(),
    },
    createElectricalProjectUserCommand: {
        electricalProjectUserId: z.string().nullable().optional(),
        electricalProjectVersionId: z.string().min(1),
        userId: z.string().min(1),
        kind: z.enum(['Inspector', 'CertifiedInspector', 'PriceQuoteSigner']),
    },
    createObjectCommand: {
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        constructionYear: z.number().min(1000).max(2100).nullable().optional(),
        street: z.string().min(1).max(64),
        streetNumber: z
            .string()
            .min(1)
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/),
        postalCode: z
            .string()
            .min(1)
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/),
        locality: z.string().min(1).max(64),
        administrativeArea: z.string().max(64).nullable().optional(),
        countryId: z.string().min(1),
        remarks: z.string().max(1000).nullable().optional(),
    },
    createObjectRflowCompanyLinkCommand: {
        objectId: z.string().min(1),
        rflowCompanyId: z.string().min(1),
        enabled: z.boolean(),
        linkAutomatically: z.boolean().nullable().optional(),
        remarks: z.string().max(500).nullable().optional(),
    },
    createObjectRflowInstallationLinkCommand: {
        objectId: z.string().min(1),
        rflowInstallationId: z.string().min(1),
        enabled: z.boolean(),
        linkAutomatically: z.boolean().nullable().optional(),
        remarks: z.string().max(500).nullable().optional(),
    },
    createOrganizationCommand: {
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        sourceKind: z.enum(['NLKVK']),
        createOrganizationCommandNLKvK: {
            kVKNumber: z
                .string()
                .min(1)
                .regex(/^[0-9]{8}$/),
            branchNumber: z
                .string()
                .regex(/^[0-9]{12}$/)
                .nullable()
                .optional(),
        },
        slogan: z.string().max(250).nullable().optional(),
        chamberOfCommerceNumber: z.string().max(20).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        iBAN: z.string().max(40).nullable().optional(),
        bIC: z.string().max(15).nullable().optional(),
        vatNumber: z.string().max(30).nullable().optional(),
        electricalOrganizationKinds: z
            .array(z.enum(['None', 'Inspection']))
            .nullable()
            .optional(),
        sciosRegistrationNumber: z.string().max(30).nullable().optional(),
        enableElectricalProjectPriceQuote: z.boolean().nullable().optional(),
        enableElectricalProjectPlanning: z.boolean().nullable().optional(),
        enableElectricalProjectRecommendations: z
            .boolean()
            .nullable()
            .optional(),
    },
    createOrganizationObjectRelationCommand: {
        organizationId: z.string().min(1),
        objectId: z.string().min(1),
        relationKind: z.enum([
            'ObjectOwner',
            'ObjectTenant',
            'ObjectAdministrator',
            'ObjectAdministratorElectricalInstallation',
            'ObjectAdministratorRefrigerantInstallation',
            'ObjectAdministratorAtex',
            'ObjectElectricalInstallationInspector',
            'ObjectRefrigerantInstallationInstaller',
        ]),
    },
    createOrganizationUserCommand: {
        organizationId: z.string().min(1),
        email: z.union([z.literal(''), z.string().email()]),
        createUserCommandSettings: {
            displayName: z.string().min(1).max(50),
            signatureText: z.string().max(50).nullable().optional(),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            phoneNumber: z.string().max(60).nullable().optional(),
            colorCode: z.string().max(30).nullable().optional(),
        },
    },
    createRepeatElectricalProjectCommand: {
        electricalProjectId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        description: z.string().max(100).nullable().optional(),
    },
    createUserCommand: {
        email: z.union([z.literal(''), z.string().email()]),
        createUserCommandSettings: {
            displayName: z.string().min(1).max(50),
            signatureText: z.string().max(50).nullable().optional(),
            firstName: z.string().max(50).nullable().optional(),
            middleName: z.string().max(20).nullable().optional(),
            lastName: z.string().max(50).nullable().optional(),
            department: z.string().max(100).nullable().optional(),
            jobTitleDescription: z.string().max(100).nullable().optional(),
            phoneNumber: z.string().max(60).nullable().optional(),
            colorCode: z.string().max(30).nullable().optional(),
        },
    },
    deleteDocumentTemplateSettingFileCommand: {
        documentTemplateSettingId: z.string().min(1),
    },
    editAccountSettingsCommand: {
        displayName: z.string().max(50).nullable().optional(),
        signatureText: z.string().max(50).nullable().optional(),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        phoneNumber: z.string().max(60).nullable().optional(),
        colorCode: z.string().max(30).nullable().optional(),
    },
    editContactPersonCommand: {
        contactPersonId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
    },
    editDocumentTemplateCommand: {
        documentTemplateId: z.string().min(1),
        name: z.string().min(1).max(100),
        description: z.string().min(1).max(500),
        groupName: z.string().max(100).nullable().optional(),
        order: z.number(),
        isDefault: z.boolean(),
        isPublic: z.boolean(),
        enabled: z.boolean(),
        editDocumentTemplateCommandPdf: {
            pageSize: z
                .enum([
                    'Letter',
                    'Legal',
                    'A4',
                    'CSheet',
                    'DSheet',
                    'ESheet',
                    'LetterSmall',
                    'Tabloid',
                    'Ledger',
                    'Statement',
                    'Executive',
                    'A3',
                    'A4Small',
                    'A5',
                    'B4',
                    'B5',
                    'Folio',
                    'Quarto',
                    'Standard10x14',
                    'Standard11x17',
                    'Note',
                    'Number9Envelope',
                    'Number10Envelope',
                    'Number11Envelope',
                    'Number12Envelope',
                    'Number14Envelope',
                    'DLEnvelope',
                    'C5Envelope',
                    'C3Envelope',
                    'C4Envelope',
                    'C6Envelope',
                    'C65Envelope',
                    'B4Envelope',
                    'B5Envelope',
                    'B6Envelope',
                    'ItalyEnvelope',
                    'MonarchEnvelope',
                    'PersonalEnvelope',
                    'USStandardFanfold',
                    'GermanStandardFanfold',
                    'GermanLegalFanfold',
                    'IsoB4',
                    'JapanesePostcard',
                    'Standard9x11',
                    'Standard10x11',
                    'Standard15x11',
                    'InviteEnvelope',
                    'LetterExtra',
                    'LegalExtra',
                    'TabloidExtra',
                    'A4Extra',
                    'LetterTransverse',
                    'A4Transverse',
                    'LetterExtraTransverse',
                    'APlus',
                    'BPlus',
                    'LetterPlus',
                    'A4Plus',
                    'A5Transverse',
                    'B5Transverse',
                    'A3Extra',
                    'A5Extra',
                    'B5Extra',
                    'A2',
                    'A3Transverse',
                    'A3ExtraTransverse',
                    'JapaneseDoublePostcard',
                    'A6',
                    'LetterRotated',
                    'A3Rotated',
                    'A4Rotated',
                    'A5Rotated',
                    'B4JisRotated',
                    'B5JisRotated',
                    'JapanesePostcardRotated',
                    'JapaneseDoublePostcardRotated',
                    'A6Rotated',
                    'B6Jis',
                    'B6JisRotated',
                    'Standard12x11',
                    'Prc16K',
                    'Prc32K',
                    'Prc32KBig',
                    'PrcEnvelopeNumber1',
                    'PrcEnvelopeNumber2',
                    'PrcEnvelopeNumber3',
                    'PrcEnvelopeNumber4',
                    'PrcEnvelopeNumber5',
                    'PrcEnvelopeNumber6',
                    'PrcEnvelopeNumber7',
                    'PrcEnvelopeNumber8',
                    'PrcEnvelopeNumber9',
                    'PrcEnvelopeNumber10',
                    'Prc16KRotated',
                    'Prc32KRotated',
                    'Prc32KBigRotated',
                    'PrcEnvelopeNumber1Rotated',
                    'PrcEnvelopeNumber2Rotated',
                    'PrcEnvelopeNumber3Rotated',
                    'PrcEnvelopeNumber4Rotated',
                    'PrcEnvelopeNumber5Rotated',
                    'PrcEnvelopeNumber6Rotated',
                    'PrcEnvelopeNumber7Rotated',
                    'PrcEnvelopeNumber8Rotated',
                    'PrcEnvelopeNumber9Rotated',
                    'PrcEnvelopeNumber10Rotated',
                    'Custom',
                ])
                .nullable()
                .optional(),
            orientation: z
                .enum(['Landscape', 'Portrait'])
                .nullable()
                .optional(),
            marginLeft: z.number().nullable().optional(),
            marginRight: z.number().nullable().optional(),
            marginTop: z.number().nullable().optional(),
            marginBottom: z.number().nullable().optional(),
            enableJavaScript: z.boolean().nullable().optional(),
            javascript: z.string().max(10000).nullable().optional(),
            firstPageNumber: z.number().nullable().optional(),
            printHtmlBackgrounds: z.boolean().nullable().optional(),
            useMarginsOnHeaderAndFooter: z
                .array(
                    z.enum([
                        'None',
                        'Top',
                        'Bottom',
                        'TopAndBottom',
                        'Left',
                        'Right',
                        'LeftAndRight',
                        'All',
                    ])
                )
                .nullable()
                .optional(),
            title: z.string().max(500).nullable().optional(),
            zoom: z.number().nullable().optional(),
            enableMathematicalLaTex: z.boolean().nullable().optional(),
            grayScale: z.boolean().nullable().optional(),
            cssMediaType: z.enum(['Print', 'Screen']).nullable().optional(),
            customCssUrl: z.string().max(250).nullable().optional(),
            createPdfFormsFromHtml: z.boolean().nullable().optional(),
            tableOfContentsTypes: z
                .enum(['None', 'Basic', 'WithPageNumbers'])
                .nullable()
                .optional(),
            forcePaperSize: z.boolean().nullable().optional(),
            fitToPaperModes: z
                .enum([
                    'Zoom',
                    'FixedPixelWidth',
                    'Automatic',
                    'AutomaticFit',
                    'ContinuousFeed',
                ])
                .nullable()
                .optional(),
            headerLoadStylesAndCSSFromMainHtmlDocument: z
                .boolean()
                .nullable()
                .optional(),
            headerUseCssFromMainDocument: z.boolean().nullable().optional(),
            headerMaxHeight: z.number().nullable().optional(),
            footerLoadStylesAndCSSFromMainHtmlDocument: z
                .boolean()
                .nullable()
                .optional(),
            footerUseCssFromMainDocument: z.boolean().nullable().optional(),
            footerMaxHeight: z.number().nullable().optional(),
        },
        editDocumentTemplateCommandTailwind: {
            gradientPrimary: z.string().max(600).nullable().optional(),
            gradientSecondary: z.string().max(600).nullable().optional(),
            backgroundPrimary: z.string().max(50).nullable().optional(),
            backgroundSecondary: z.string().max(50).nullable().optional(),
            backgroundTertiary: z.string().max(50).nullable().optional(),
            backgroundQuaternary: z.string().max(50).nullable().optional(),
            backgroundPopover: z.string().max(50).nullable().optional(),
            backgroundTab: z.string().max(50).nullable().optional(),
            inputBorder: z.string().max(50).nullable().optional(),
            inputBorderShadow: z.string().max(50).nullable().optional(),
            textPrimary: z.string().max(50).nullable().optional(),
            textSecondary: z.string().max(50).nullable().optional(),
            textTertiary: z.string().max(50).nullable().optional(),
            textQuaternary: z.string().max(50).nullable().optional(),
            iconPrimary: z.string().max(50).nullable().optional(),
            iconSecondary: z.string().max(50).nullable().optional(),
            brandPrimary: z.string().max(50).nullable().optional(),
            brandSecondary: z.string().max(50).nullable().optional(),
            link: z.string().max(50).nullable().optional(),
            radius: z.string().max(50).nullable().optional(),
            fontSizeH1: z.string().max(50).nullable().optional(),
            fontSizeH2: z.string().max(50).nullable().optional(),
            fontSizeH3: z.string().max(50).nullable().optional(),
            fontSizeH4: z.string().max(50).nullable().optional(),
            fontSizeH5: z.string().max(50).nullable().optional(),
            fontSizeH6: z.string().max(50).nullable().optional(),
            fontSizeP: z.string().max(50).nullable().optional(),
            fontSizeLi: z.string().max(50).nullable().optional(),
            fontSizeTd: z.string().max(50).nullable().optional(),
            fontFamily: z.string().max(50).nullable().optional(),
            lineHeightH1: z.string().max(50).nullable().optional(),
            lineHeightH2: z.string().max(50).nullable().optional(),
            lineHeightH3: z.string().max(50).nullable().optional(),
            lineHeightH4: z.string().max(50).nullable().optional(),
            lineHeightH5: z.string().max(50).nullable().optional(),
            lineHeightH6: z.string().max(50).nullable().optional(),
            lineHeightP: z.string().max(50).nullable().optional(),
            lineHeightLi: z.string().max(50).nullable().optional(),
            lineHeightTd: z.string().max(50).nullable().optional(),
            fontWeightH1: z.string().max(50).nullable().optional(),
            fontWeightH2: z.string().max(50).nullable().optional(),
            fontWeightH3: z.string().max(50).nullable().optional(),
            fontWeightH4: z.string().max(50).nullable().optional(),
            fontWeightH5: z.string().max(50).nullable().optional(),
            fontWeightH6: z.string().max(50).nullable().optional(),
            fontWeightP: z.string().max(50).nullable().optional(),
            fontWeightLi: z.string().max(50).nullable().optional(),
            fontWeightTd: z.string().max(50).nullable().optional(),
            indentLength: z.string().max(50).nullable().optional(),
        },
        editDocumentTemplateCommandSetting: {
            documentTemplateSettingId: z.string().min(1),
            enabled: z.boolean(),
            imageQuality: z
                .enum([
                    'Original',
                    'OriginalWebp',
                    'ExtraLarge',
                    'Large',
                    'Medium',
                    'Small',
                    'ExtraSmall',
                ])
                .nullable()
                .optional(),
            toggle: z.boolean().nullable().optional(),
            wholeNumber: z.number().nullable().optional(),
            decimalNumber: z
                .number()
                .min(-1000000000)
                .max(1000000000)
                .nullable()
                .optional(),
            date: z.date().nullable().optional(),
            dateTime: z.date().nullable().optional(),
            dateTimeOffset: z.date().nullable().optional(),
            text: z.string().max(200000).nullable().optional(),
        },
    },
    editElectricalCheckListCommand: {
        electricalCheckListId: z.string().min(1),
        description: z.string().min(1).max(100),
    },
    editElectricalCheckListItemCommand: {
        electricalCheckListItemId: z.string().min(1),
        enableNotApplicable: z.boolean().nullable().optional(),
        enableObservations: z.boolean().nullable().optional(),
        description: z.string().min(1).max(1000),
        positiveLabelText: z.string().max(20).nullable().optional(),
        negativeLabelText: z.string().max(20).nullable().optional(),
        notApplicableLabelText: z.string().max(20).nullable().optional(),
        order: z.number(),
    },
    editElectricalCheckListTextCommand: {
        electricalCheckListTextId: z.string().min(1),
        text: z.string().min(1).max(1000),
        editElectricalCheckListTextCommandReport: {
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        },
    },
    editElectricalInspectionNormItemCommand: {
        electricalInspectionNormItemId: z.string().min(1),
        normKind: z.enum(['NEN', 'SCIOS', 'NPR', 'NTA', 'Other']),
        inspectionKinds: z
            .array(
                z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
            )
            .nullable()
            .optional(),
        name: z.string().max(1000).nullable().optional(),
        description: z.string().max(1000).nullable().optional(),
        order: z.number(),
        isCustomInput: z.boolean(),
    },
    editElectricalInspectionNormListCommand: {
        electricalInspectionNormListId: z.string().min(1),
        description: z.string().min(1).max(100),
    },
    editElectricalMeasurementDeviceCommand: {
        electricalMeasurementDeviceId: z.string().min(1),
        userId: z.string().nullable().optional(),
        name: z.string().min(1).max(300),
        startDate: z.date().nullable().optional(),
        endDate: z.date().nullable().optional(),
        inspectionKinds: z
            .array(
                z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
            )
            .nullable()
            .optional(),
    },
    editElectricalObservationStandardTextItemCommand: {
        electricalObservationStandardTextItemId: z.string().min(1),
        description: z.string().min(1).max(1000),
        order: z.number(),
        editElectricalObservationStandardTextItemCommandReport: {
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            groupKind: z
                .enum(['A', 'B', 'C', 'D', 'E', 'F'])
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        },
    },
    editElectricalProjectCheckListItemCommand: {
        electricalProjectCheckListItemId: z.string().min(1),
        conclusionKind: z
            .enum(['Approved', 'NotApproved', 'NotApplicable'])
            .nullable()
            .optional(),
    },
    editElectricalProjectContactPersonCommand: {
        electricalProjectContactPersonId: z.string().min(1),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        receivePriceQuote: z.boolean().nullable().optional(),
        receiveInvoice: z.boolean().nullable().optional(),
        receiveInspectionPlan: z.boolean().nullable().optional(),
        receiveInspectionReport: z.boolean().nullable().optional(),
        receivePowerShutdownPlan: z.boolean().nullable().optional(),
        isResponsibleForPowerSystem: z.boolean().nullable().optional(),
    },
    editElectricalProjectDistributionBoardCommand: {
        electricalProjectDistributionBoardId: z.string().min(1),
        kind: z
            .enum([
                'MainDistributionBoard',
                'SubDistributionBoard',
                'ControlBox',
            ])
            .nullable()
            .optional(),
        isPVInstallation: z.boolean().nullable().optional(),
        parentElectricalProjectDistributionBoardId: z
            .string()
            .nullable()
            .optional(),
        order: z.number(),
        identifier: z.string().max(100).nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        earthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        electricalIngressProtectionRatingId: z.string().nullable().optional(),
        customElectricalIngressProtectionRating: z
            .string()
            .nullable()
            .optional(),
        applicanceProtectionClass: z
            .enum(['0', '0I', 'I', 'II', 'IIFE', 'III'])
            .nullable()
            .optional(),
        mainSwitchNominalCurrent: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        upstreamProtectiveDeviceKind: z
            .enum([
                'DiazedFuse',
                'FuseLink',
                'CircuitBreaker',
                'Unknown',
                'PowerCircuitBreaker',
            ])
            .nullable()
            .optional(),
        upstreamProtectiveDeviceCharacteristic: z
            .string()
            .max(50)
            .nullable()
            .optional(),
        powerCircuitBreakerType: z.string().max(100).nullable().optional(),
        powerCircuitBreakerSetting: z.string().max(100).nullable().optional(),
        powerCircuitBreakerCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentNeutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageNeutralGround: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        powerPhaseRotation: z
            .enum(['Clockwise', 'CounterClockwise', 'NotApplicable'])
            .nullable()
            .optional(),
        groundImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        measureImpedanceBetweenPowerPhases: z.boolean().nullable().optional(),
        impedanceCurrentL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCurrentMeasurements: z.boolean().nullable().optional(),
        reportVoltageMeasurements: z.boolean().nullable().optional(),
        reportGroundImpedanceMeasurements: z.boolean().nullable().optional(),
        reportNeutralImpedanceMeasurements: z.boolean().nullable().optional(),
        reportResidualCurrentDevices: z.boolean().nullable().optional(),
        reportResidualCurrentCircuitBreakerDevices: z
            .boolean()
            .nullable()
            .optional(),
        reportFinalCircuitProtectiveDevices: z.boolean().nullable().optional(),
        reportIsolationResistanceMeasurements: z
            .boolean()
            .nullable()
            .optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
    },
    editElectricalProjectDistributionBoardDeviceCommand: {
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        residualCurrentDeviceType: z.string().max(50).nullable().optional(),
        ratedCurrent: z.number().nullable().optional(),
        tripDelay: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ratedResidualCurrent: z.number().nullable().optional(),
        fuseKind: z.string().max(50).nullable().optional(),
        trippingCharacteristic: z.string().max(50).nullable().optional(),
        breakerRatedCurrent: z.number().nullable().optional(),
        isTestButtonFunctional: z.boolean().nullable().optional(),
        resistanceL1PE: z.number().nullable().optional(),
        resistanceL2PE: z.number().nullable().optional(),
        resistanceL3PE: z.number().nullable().optional(),
        resistanceNeutralPE: z.number().nullable().optional(),
        resistanceL1Neutral: z.number().nullable().optional(),
        resistanceL2Neutral: z.number().nullable().optional(),
        resistanceL3Neutral: z.number().nullable().optional(),
        measureResistanceBetweenPowerPhases: z.boolean().nullable().optional(),
        resistanceL1L2: z.number().nullable().optional(),
        resistanceL1L3: z.number().nullable().optional(),
        resistanceL2L3: z.number().nullable().optional(),
        risoSign: z.enum(['<', '>', '=']).nullable().optional(),
        risoValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportRisoMeasurements: z.boolean().nullable().optional(),
    },
    editElectricalProjectDistributionBoardFileCommand: {
        electricalProjectDistributionBoardFileId: z.string().min(1),
        electricalProjectFileId: z.string().nullable().optional(),
        order: z.number(),
        remark: z.string().max(100).nullable().optional(),
    },
    editElectricalProjectDocumentTemplateCommand: {
        electricalProjectVersionId: z.string().min(1),
        editElectricalProjectDocumentTemplateCommandTemplate: {
            electricalProjectDocumentTemplateId: z.string().min(1),
            selected: z.boolean(),
        },
    },
    editElectricalProjectEquipotentialBondingMeasurementCommand: {
        electricalProjectEquipotentialBondingMeasurementId: z.string().min(1),
        location: z.string().max(100).nullable().optional(),
        resistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        order: z.number(),
    },
    editElectricalProjectFileCommand: {
        electricalProjectFileId: z.string().min(1),
        order: z.number(),
        description: z.string().max(200).nullable().optional(),
    },
    editElectricalProjectFinalCircuitCommand: {
        electricalProjectFinalCircuitId: z.string().min(1),
        kind: z
            .enum([
                'FinalCircuit',
                'PhotovoltaicInverter',
                'Machine',
                'Space',
                'Building',
                'Other',
            ])
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
    },
    editElectricalProjectInspectionCommand: {
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z
            .enum(['Inspection', 'RepeatInspection'])
            .nullable()
            .optional(),
        reportNumber: z.string().max(100).nullable().optional(),
        distributionBoardMeasurementRegistrationKind: z
            .enum(['Input', 'Attachment', 'Request'])
            .nullable()
            .optional(),
        enableExtendedRisoOnDistributionBoardDevices: z
            .boolean()
            .nullable()
            .optional(),
        pVSolarIrradiance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        pVSolarPanelTemperature: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isPVInstallationEquipotentialBondingPassed: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationEquipotentialBondingPassedRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isPVInstallationConstructionCompanyKnown: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationConstructionDocumentNumber: z
            .string()
            .max(100)
            .nullable()
            .optional(),
    },
    editElectricalProjectInspectionConclusionCommand: {
        electricalProjectVersionId: z.string().min(1),
        editElectricalProjectInspectionConclusionCommandScope: {
            sciosInspectionCode: z.string().max(20).nullable().optional(),
        },
        editElectricalProjectInspectionConclusionCommandIntervalQuestions: {
            determineInspectionIntervalAge: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalQuality: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalEnvironment: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalUsers: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalSupervision: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
        },
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        scope10InspectionIntervalTableCellId: z.number().nullable().optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
    },
    editElectricalProjectInspectionConclusionReportCommand: {
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        sciosInspectionCode: z.string().max(20).nullable().optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
    },
    editElectricalProjectInspectionNormCommand: {
        electricalProjectInspectionNormId: z.string().min(1),
        status: z.enum(['Selected', 'NotSelected', 'NotApplicable']),
        inspectionKinds: z
            .array(
                z.enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
            )
            .nullable()
            .optional(),
        customDescription: z.string().max(1000).nullable().optional(),
        version: z.string().max(100).nullable().optional(),
        remarks: z.string().max(1000).nullable().optional(),
    },
    editElectricalProjectInspectionPlanCommand: {
        electricalProjectVersionId: z.string().min(1),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
        shutdownMethodKind: z
            .enum([
                'ConformDocumentSuppliedByInstallationAdministrator',
                'CustomerPerformsPowerShutdown',
                'InspectionCompanyIsAuthorized',
                'CustomerDoesNotGivePermission',
                'Other',
            ])
            .nullable()
            .optional(),
        shutdownMethodOther: z.string().max(1000).nullable().optional(),
        shutdownMethodRemark: z.string().max(1000).nullable().optional(),
        pVInstallationTypologyKind: z
            .enum([
                'StringOrCentralInverter',
                'OptimizersWithInverters',
                'MicroInverters',
            ])
            .nullable()
            .optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
    },
    editElectricalProjectInspectionPlanRemarkCommand: {
        electricalProjectInspectionPlanRemarkId: z.string().min(1),
        order: z.number(),
        subject: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
    },
    editElectricalProjectInspectionPlanReportCommand: {
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        enabled: z.boolean(),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
    },
    editElectricalProjectInspectionScheduleCommand: {
        electricalProjectVersionId: z.string().min(1),
        isOrderConfirmed: z.boolean().nullable().optional(),
        orderConfirmationRemark: z.string().max(500).nullable().optional(),
        startDate: z.date().nullable().optional(),
        endDate: z.date().nullable().optional(),
    },
    editElectricalProjectMeasurementDeviceCommand: {
        electricalProjectMeasurementDeviceId: z.string().min(1),
        inspectionKinds: z.array(
            z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ])
        ),
    },
    editElectricalProjectObjectAddressCommand: {
        electricalProjectVersionId: z.string().min(1),
        street: z.string().min(1).max(64),
        streetNumber: z
            .string()
            .min(1)
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/),
        postalCode: z
            .string()
            .min(1)
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/),
        locality: z.string().min(1).max(64),
    },
    editElectricalProjectObjectCommand: {
        electricalProjectVersionId: z.string().min(1),
        bagIdentificationNumber: z.string().max(30).nullable().optional(),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        street: z.string().max(64).nullable().optional(),
        streetNumber: z
            .string()
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
            .nullable()
            .optional(),
        postalCode: z
            .string()
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
            .nullable()
            .optional(),
        locality: z.string().max(64).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        buildingConstructionYear: z
            .number()
            .min(1000)
            .max(2100)
            .nullable()
            .optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        buildingCount: z.number().min(0).max(100000).nullable().optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(100000).nullable().optional(),
        isElectricalInstallationConstructionYearKnown: z
            .boolean()
            .nullable()
            .optional(),
        electricalInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        remarks: z.string().max(1000).nullable().optional(),
        inspectionPlanCreatorKind: z
            .enum([
                'InstallationAdministrator',
                'BuildingOwnerOrAdministrator',
                'Customer',
                'InspectionCompany',
            ])
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(100000).nullable().optional(),
        pVInverterCount: z.number().min(0).max(100000).nullable().optional(),
        pVPanelsTotalPower: z.number().min(0).max(10000).nullable().optional(),
        pVInvertersTotalPower: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pVBuildKind: z
            .enum(['InRoof', 'OnRoof', 'OnField', 'OnWater'])
            .nullable()
            .optional(),
        pVInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        pVSpecialSpaceKind: z
            .enum(['New', 'Existing', 'Expanded'])
            .nullable()
            .optional(),
        pVEarthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        pVMainsVoltage: z.number().min(0).max(10000).nullable().optional(),
        pVInstallationHasLightningProtection: z.boolean().nullable().optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
        pVAreSpecialSpacesPresent: z.boolean().nullable().optional(),
    },
    editElectricalProjectObservationCommand: {
        electricalProjectObservationId: z.string().min(1),
        order: z.number(),
        floorplanElectricalProjectFileId: z.string().nullable().optional(),
        floorplanPinCoordinateX: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        floorplanPinCoordinateY: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        locationRemark: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
        editElectricalProjectObservationCommandReport: {
            inspectionKind: z.enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ]),
            enabled: z.boolean(),
            remarkKind: z.enum(['None', 'Same', 'Custom']),
            remark: z.string().max(1000).nullable().optional(),
            recommendationKind: z.enum(['None', 'Same', 'Custom']),
            recommendation: z.string().max(1000).nullable().optional(),
            electricalObservationClassificationGroupId: z
                .string()
                .nullable()
                .optional(),
            electricalObservationClassificationCodeId: z
                .string()
                .nullable()
                .optional(),
            codeKind: z
                .enum(['Severe', 'Serious', 'Minor', 'Remark'])
                .nullable()
                .optional(),
        },
    },
    editElectricalProjectOrganizationCommand: {
        electricalProjectOrganizationId: z.string().min(1),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        editElectricalProjectOrganizationCommandAddress: {
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            administrativeArea: z.string().max(64).nullable().optional(),
            countryId: z.string().nullable().optional(),
        },
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        isProjectCustomer: z.boolean(),
        isObjectOwner: z.boolean(),
        isObjectAdministrator: z.boolean(),
        isObjectTenant: z.boolean(),
        isPVInstallationConstructionCompany: z.boolean().nullable().optional(),
    },
    editElectricalProjectPriceQuoteCommand: {
        electricalProjectVersionId: z.string().min(1),
        outbuildingCount: z.number().min(0).max(1000).nullable().optional(),
        basePrice: z.number().min(0).max(100000).nullable().optional(),
        squareMeterCount: z.number().min(0).max(10000000).nullable().optional(),
        pricePerSquareMeter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pricePerDistributionBoard: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerControlBox: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRemoteRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        discountPercentage: z.number().min(0).max(25).nullable().optional(),
        pVInverterCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVInverter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVPanel: z.number().min(0).max(10000000).nullable().optional(),
        priceQuoteDate: z.date().nullable().optional(),
    },
    editElectricalProjectPVInverterCommand: {
        electricalProjectPVInverterId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        brandModel: z.string().max(100).nullable().optional(),
        serialNumber: z.string().max(100).nullable().optional(),
        firmwareVersion: z.string().max(100).nullable().optional(),
        ratedPower: z.number().min(0).max(100000).nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        order: z.number(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportImpedanceMeasurements: z.boolean().nullable().optional(),
        reportPVStrings: z.boolean().nullable().optional(),
    },
    editElectricalProjectPVStringCommand: {
        electricalProjectPVStringId: z.string().min(1),
        identifier: z.string().max(100).nullable().optional(),
        voltage: z.number().min(0).max(100000).nullable().optional(),
        resistance: z.number().min(0).max(100000).nullable().optional(),
        current: z.number().min(0).max(100000).nullable().optional(),
        order: z.number(),
    },
    editElectricalProjectRisoMeasurementCommand: {
        electricalProjectRisoMeasurementId: z.string().min(1),
        circuitKind: z
            .enum([
                'L-PE / N-PE',
                'L1-L2',
                'L2-L3',
                'L1-L3',
                'L1-N',
                'L2-N',
                'L3-N',
                'L1-PE',
                'L2-PE',
                'L3-PE',
                'N-PE',
                'L-PE',
            ])
            .nullable()
            .optional(),
        measuredValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        minimumValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isFaulty: z.boolean().nullable().optional(),
        order: z.number(),
    },
    editElectricalProjectSelectListItemCommand: {
        electricalProjectVersionId: z.string().min(1),
        listKind: z.enum([
            'InspectionExclusion',
            'ObjectSpecialSpace',
            'AdditionalInstruction',
            'AdditionalTopic',
            'InspectionComponent',
            'BuildingFunction',
        ]),
        editElectricalProjectSelectListItemCommandItem: {
            electricalProjectSelectListItemId: z.string().min(1),
            selected: z.boolean().nullable().optional(),
            inspectionKinds: z
                .array(
                    z.enum([
                        'None',
                        'Scope8',
                        'Scope10',
                        'Scope12',
                        'Nen1010',
                        'Nen3140',
                    ])
                )
                .nullable()
                .optional(),
            customValue: z.string().max(500).nullable().optional(),
        },
    },
    editElectricalProjectTechnicalDrawingCommand: {
        electricalProjectTechnicalDrawingId: z.string().min(1),
        isDrawingPresent: z.boolean().nullable().optional(),
        electricalProjectFileId: z.string().nullable().optional(),
        name: z.string().min(1).max(200),
        location: z.string().max(200).nullable().optional(),
        description: z.string().max(500).nullable().optional(),
        order: z.number(),
    },
    editElectricalProjectUserCommand: {
        electricalProjectVersionId: z.string().min(1),
        userId: z.string().min(1),
        kind: z.enum(['Inspector', 'CertifiedInspector', 'PriceQuoteSigner']),
        selected: z.boolean(),
    },
    editObjectCommand: {
        objectId: z.string().min(1),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        constructionYear: z.number().min(1000).max(2100).nullable().optional(),
        remarks: z.string().max(1000).nullable().optional(),
    },
    editObjectRflowLinkCommand: {
        objectRflowLinkId: z.string().min(1),
        enabled: z.boolean(),
        linkAutomatically: z.boolean().nullable().optional(),
        remarks: z.string().max(500).nullable().optional(),
    },
    editOrganizationCommand: {
        organizationId: z.string().min(1),
        name: z.string().min(1).max(100),
        description: z.string().max(250).nullable().optional(),
        slogan: z.string().max(250).nullable().optional(),
        chamberOfCommerceNumber: z.string().max(20).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        iBAN: z.string().max(40).nullable().optional(),
        bIC: z.string().max(15).nullable().optional(),
        vatNumber: z.string().max(30).nullable().optional(),
        electricalOrganizationKinds: z
            .array(z.enum(['None', 'Inspection']))
            .nullable()
            .optional(),
        sciosRegistrationNumber: z.string().max(30).nullable().optional(),
        enableElectricalProjectPriceQuote: z.boolean().nullable().optional(),
        enableElectricalProjectPlanning: z.boolean().nullable().optional(),
        enableElectricalProjectRecommendations: z
            .boolean()
            .nullable()
            .optional(),
    },
    editOrganizationFileCommand: {
        organizationFileId: z.string().min(1),
        order: z.number(),
        description: z.string().max(200).nullable().optional(),
    },
    editUserFileCommand: {
        userFileId: z.string().min(1),
        order: z.number(),
        description: z.string().max(200).nullable().optional(),
    },
    editUserSettingsCommand: {
        userId: z.string().min(1),
        displayName: z.string().max(50).nullable().optional(),
        signatureText: z.string().max(50).nullable().optional(),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        phoneNumber: z.string().max(60).nullable().optional(),
        colorCode: z.string().max(30).nullable().optional(),
    },
    forgotAccountPasswordCommand: {
        email: z.union([z.literal(''), z.string().email()]),
    },
    oAuth2TokenV2Request: {
        grant_type: z.string().min(1),
        username: z.string().min(1),
        password: z.string().min(100),
    },
    oAuth2TokenV2ResponseBadRequest: {
        error: z.string().nullable().optional(),
        error_description: z.string().nullable().optional(),
        error_uri: z.string().nullable().optional(),
    },
    patchElectricalProjectContactPersonCommand: {
        electricalProjectContactPersonId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'FirstName',
                    'MiddleName',
                    'LastName',
                    'Department',
                    'JobTitleDescription',
                    'Email',
                    'MobilePhoneNumber',
                ])
            )
            .nullable()
            .optional(),
        firstName: z.string().max(50).nullable().optional(),
        middleName: z.string().max(20).nullable().optional(),
        lastName: z.string().max(50).nullable().optional(),
        department: z.string().max(100).nullable().optional(),
        jobTitleDescription: z.string().max(100).nullable().optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        mobilePhoneNumber: z.string().max(60).nullable().optional(),
        receivePriceQuote: z.boolean().nullable().optional(),
        receiveInvoice: z.boolean().nullable().optional(),
        receiveInspectionPlan: z.boolean().nullable().optional(),
        receiveInspectionReport: z.boolean().nullable().optional(),
        receivePowerShutdownPlan: z.boolean().nullable().optional(),
        isResponsibleForPowerSystem: z.boolean().nullable().optional(),
    },
    patchElectricalProjectDistributionBoardCommand: {
        electricalProjectDistributionBoardId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'Kind',
                    'ParentElectricalProjectDistributionBoardId',
                    'Identifier',
                    'FloorNumber',
                    'SpaceNumber',
                    'InternalRemark',
                    'InternalImageRemark',
                    'InternalThermalImageRemark',
                    'EarthingSystemKind',
                    'PowerPhaseKind',
                    'ElectricalIngressProtectionRatingId',
                    'CustomElectricalIngressProtectionRating',
                    'ApplicanceProtectionClass',
                    'MainSwitchNominalCurrent',
                    'UpstreamProtectiveDeviceKind',
                    'UpstreamProtectiveDeviceCharacteristic',
                    'PowerCircuitBreakerType',
                    'PowerCircuitBreakerSetting',
                    'PowerCircuitBreakerCurrent',
                    'CurrentL1',
                    'CurrentL2',
                    'CurrentL3',
                    'CurrentNeutral',
                    'VoltageL1',
                    'VoltageL2',
                    'VoltageL3',
                    'VoltageL1Neutral',
                    'VoltageL2Neutral',
                    'VoltageL3Neutral',
                    'VoltageL1Ground',
                    'VoltageL2Ground',
                    'VoltageL3Ground',
                    'VoltageNeutralGround',
                    'PowerPhaseRotation',
                    'GroundImpedanceCurrentL1',
                    'GroundImpedanceCurrentL2',
                    'GroundImpedanceCurrentL3',
                    'GroundImpedanceL1',
                    'GroundImpedanceL2',
                    'GroundImpedanceL3',
                    'NeutralImpedanceCurrentL1',
                    'NeutralImpedanceCurrentL2',
                    'NeutralImpedanceCurrentL3',
                    'NeutralImpedanceL1',
                    'NeutralImpedanceL2',
                    'NeutralImpedanceL3',
                    'MeasureImpedanceBetweenPowerPhases',
                    'ImpedanceCurrentL1L2',
                    'ImpedanceCurrentL1L3',
                    'ImpedanceCurrentL2L3',
                    'ImpedanceL1L2',
                    'ImpedanceL1L3',
                    'ImpedanceL2L3',
                ])
            )
            .nullable()
            .optional(),
        kind: z
            .enum([
                'MainDistributionBoard',
                'SubDistributionBoard',
                'ControlBox',
            ])
            .nullable()
            .optional(),
        isPVInstallation: z.boolean().nullable().optional(),
        parentElectricalProjectDistributionBoardId: z
            .string()
            .nullable()
            .optional(),
        order: z.number().nullable().optional(),
        identifier: z.string().max(100).nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        earthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        electricalIngressProtectionRatingId: z.string().nullable().optional(),
        customElectricalIngressProtectionRating: z
            .string()
            .nullable()
            .optional(),
        applicanceProtectionClass: z
            .enum(['0', '0I', 'I', 'II', 'IIFE', 'III'])
            .nullable()
            .optional(),
        mainSwitchNominalCurrent: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        upstreamProtectiveDeviceKind: z
            .enum([
                'DiazedFuse',
                'FuseLink',
                'CircuitBreaker',
                'Unknown',
                'PowerCircuitBreaker',
            ])
            .nullable()
            .optional(),
        upstreamProtectiveDeviceCharacteristic: z
            .string()
            .max(50)
            .nullable()
            .optional(),
        powerCircuitBreakerType: z.string().max(100).nullable().optional(),
        powerCircuitBreakerSetting: z.string().max(100).nullable().optional(),
        powerCircuitBreakerCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        currentNeutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Neutral: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL1Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL2Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageL3Ground: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        voltageNeutralGround: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        powerPhaseRotation: z
            .enum(['Clockwise', 'CounterClockwise', 'NotApplicable'])
            .nullable()
            .optional(),
        groundImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceCurrentL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        measureImpedanceBetweenPowerPhases: z.boolean().nullable().optional(),
        impedanceCurrentL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceCurrentL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCurrentMeasurements: z.boolean().nullable().optional(),
        reportVoltageMeasurements: z.boolean().nullable().optional(),
        reportGroundImpedanceMeasurements: z.boolean().nullable().optional(),
        reportNeutralImpedanceMeasurements: z.boolean().nullable().optional(),
        reportResidualCurrentDevices: z.boolean().nullable().optional(),
        reportResidualCurrentCircuitBreakerDevices: z
            .boolean()
            .nullable()
            .optional(),
        reportFinalCircuitProtectiveDevices: z.boolean().nullable().optional(),
        reportIsolationResistanceMeasurements: z
            .boolean()
            .nullable()
            .optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
    },
    patchElectricalProjectDistributionBoardDeviceCommand: {
        electricalProjectDistributionBoardDeviceId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'Identifier',
                    'PowerPhaseKind',
                    'ResidualCurrentDeviceType',
                    'RatedCurrent',
                    'TripDelay',
                    'RatedResidualCurrent',
                    'FuseKind',
                    'TrippingCharacteristic',
                    'BreakerRatedCurrent',
                    'IsTestButtonFunctional',
                    'ResistanceL1PE',
                    'ResistanceL2PE',
                    'ResistanceL3PE',
                    'ResistanceNeutralPE',
                    'ResistanceL1Neutral',
                    'ResistanceL2Neutral',
                    'ResistanceL3Neutral',
                    'MeasureResistanceBetweenPowerPhases',
                    'ResistanceL1L2',
                    'ResistanceL1L3',
                    'ResistanceL2L3',
                    'RisoSign',
                    'RisoValue',
                    'ZiResistance',
                    'ZiCurrent',
                    'ZiMinimalCurrent',
                    'ZsResistance',
                    'ZsCurrent',
                    'ZsMinimalCurrent',
                ])
            )
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        residualCurrentDeviceType: z.string().max(50).nullable().optional(),
        ratedCurrent: z.number().nullable().optional(),
        tripDelay: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ratedResidualCurrent: z.number().nullable().optional(),
        fuseKind: z.string().max(50).nullable().optional(),
        trippingCharacteristic: z.string().max(50).nullable().optional(),
        breakerRatedCurrent: z.number().nullable().optional(),
        isTestButtonFunctional: z.boolean().nullable().optional(),
        resistanceL1PE: z.number().nullable().optional(),
        resistanceL2PE: z.number().nullable().optional(),
        resistanceL3PE: z.number().nullable().optional(),
        resistanceNeutralPE: z.number().nullable().optional(),
        resistanceL1Neutral: z.number().nullable().optional(),
        resistanceL2Neutral: z.number().nullable().optional(),
        resistanceL3Neutral: z.number().nullable().optional(),
        measureResistanceBetweenPowerPhases: z.boolean().nullable().optional(),
        resistanceL1L2: z.number().nullable().optional(),
        resistanceL1L3: z.number().nullable().optional(),
        resistanceL2L3: z.number().nullable().optional(),
        risoSign: z.enum(['<', '>', '=']).nullable().optional(),
        risoValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        ziMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsResistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        zsMinimalCurrent: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportRisoMeasurements: z.boolean().nullable().optional(),
    },
    patchElectricalProjectDistributionBoardFileCommand: {
        electricalProjectDistributionBoardFileId: z.string().min(1),
        clearFields: z
            .array(z.enum(['ElectricalProjectFileId', 'Remark']))
            .nullable()
            .optional(),
        electricalProjectFileId: z.string().nullable().optional(),
        order: z.number().nullable().optional(),
        remark: z.string().max(100).nullable().optional(),
    },
    patchElectricalProjectEquipotentialBondingMeasurementCommand: {
        electricalProjectEquipotentialBondingMeasurementId: z.string().min(1),
        clearFields: z
            .array(z.enum(['Location', 'Resistance']))
            .nullable()
            .optional(),
        location: z.string().max(100).nullable().optional(),
        resistance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        order: z.number(),
    },
    patchElectricalProjectFinalCircuitCommand: {
        electricalProjectFinalCircuitId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'Kind',
                    'Identifier',
                    'FloorNumber',
                    'SpaceNumber',
                    'InternalRemark',
                    'InternalImageRemark',
                    'InternalThermalImageRemark',
                    'ElectricalProjectDistributionBoardId',
                ])
            )
            .nullable()
            .optional(),
        kind: z
            .enum([
                'FinalCircuit',
                'PhotovoltaicInverter',
                'Machine',
                'Space',
                'Building',
                'Other',
            ])
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        order: z.number().nullable().optional(),
        floorNumber: z.string().max(50).nullable().optional(),
        spaceNumber: z.string().max(50).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportCheckListItems: z.boolean().nullable().optional(),
    },
    patchElectricalProjectInspectionCommand: {
        electricalProjectVersionId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'InspectionKind',
                    'ReportNumber',
                    'DistributionBoardMeasurementRegistrationKind',
                    'PVSolarIrradiance',
                    'PVSolarPanelTemperature',
                    'IsPVInstallationEquipotentialBondingPassed',
                    'IsPVInstallationConstructionCompanyKnown',
                    'PVInstallationConstructionDocumentNumber',
                ])
            )
            .nullable()
            .optional(),
        inspectionKind: z
            .enum(['Inspection', 'RepeatInspection'])
            .nullable()
            .optional(),
        reportNumber: z.string().max(100).nullable().optional(),
        distributionBoardMeasurementRegistrationKind: z
            .enum(['Input', 'Attachment', 'Request'])
            .nullable()
            .optional(),
        enableExtendedRisoOnDistributionBoardDevices: z
            .boolean()
            .nullable()
            .optional(),
        pVSolarIrradiance: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        pVSolarPanelTemperature: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isPVInstallationEquipotentialBondingPassed: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationEquipotentialBondingPassedRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isPVInstallationConstructionCompanyKnown: z
            .boolean()
            .nullable()
            .optional(),
        pVInstallationConstructionDocumentNumber: z
            .string()
            .max(100)
            .nullable()
            .optional(),
    },
    patchElectricalProjectInspectionConclusionCommand: {
        electricalProjectVersionId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'SciosInspectionCode',
                    'DetermineInspectionIntervalAge',
                    'DetermineInspectionIntervalQuality',
                    'DetermineInspectionIntervalEnvironment',
                    'DetermineInspectionIntervalUsers',
                    'DetermineInspectionIntervalSupervision',
                    'NextInspectionInYearCount',
                    'Scope10InspectionIntervalTableCellId',
                    'IntervalDeterminedByKind',
                    'IncludeRepairStatement',
                    'IsPerformedAccordingToInspectionPlan',
                    'IsFinalVerdictPassed',
                ])
            )
            .nullable()
            .optional(),
        patchElectricalProjectInspectionConclusionCommandScope: {
            sciosInspectionCode: z.string().max(20).nullable().optional(),
        },
        patchElectricalProjectInspectionConclusionCommandIntervalQuestions: {
            determineInspectionIntervalAge: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalQuality: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalEnvironment: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalUsers: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
            determineInspectionIntervalSupervision: z
                .enum([
                    'Age1',
                    'Age2',
                    'Age3',
                    'Age4',
                    'Quality1',
                    'Quality2',
                    'Quality3',
                    'Quality4',
                    'Quality5',
                    'Environment1',
                    'Environment2',
                    'Environment3',
                    'Users1',
                    'Users2',
                    'Users3',
                    'Users4',
                    'Supervision1',
                    'Supervision2',
                ])
                .nullable()
                .optional(),
        },
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        scope10InspectionIntervalTableCellId: z.number().nullable().optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
    },
    patchElectricalProjectInspectionConclusionReportCommand: {
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        clearFields: z
            .array(
                z.enum([
                    'NextInspectionInYearCount',
                    'IntervalDeterminedByKind',
                    'SciosInspectionCode',
                    'IncludeRepairStatement',
                    'IsPerformedAccordingToInspectionPlan',
                    'IsFinalVerdictPassed',
                ])
            )
            .nullable()
            .optional(),
        nextInspectionInYearCount: z
            .number()
            .min(0)
            .max(15)
            .nullable()
            .optional(),
        intervalDeterminedByKind: z
            .enum(['InsuranceCompany', 'Inspection', 'RentalContract'])
            .nullable()
            .optional(),
        sciosInspectionCode: z.string().max(20).nullable().optional(),
        includeRepairStatement: z.boolean().nullable().optional(),
        isPerformedAccordingToInspectionPlan: z.boolean().nullable().optional(),
        performedAccordingToInspectionPlanRemark: z
            .string()
            .max(500)
            .nullable()
            .optional(),
        isFinalVerdictPassed: z.boolean().nullable().optional(),
        finalVerdictRemark: z.string().max(500).nullable().optional(),
    },
    patchElectricalProjectInspectionNormCommand: {
        electricalProjectInspectionNormId: z.string().min(1),
        clearFields: z
            .array(z.enum(['CustomDescription', 'Version', 'Remarks']))
            .nullable()
            .optional(),
        status: z
            .enum(['Selected', 'NotSelected', 'NotApplicable'])
            .nullable()
            .optional(),
        addInspectionKind: z
            .enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ])
            .nullable()
            .optional(),
        removeInspectionKind: z
            .enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ])
            .nullable()
            .optional(),
        customDescription: z.string().max(1000).nullable().optional(),
        version: z.string().max(100).nullable().optional(),
        remarks: z.string().max(1000).nullable().optional(),
    },
    patchElectricalProjectInspectionPlanCommand: {
        electricalProjectVersionId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'InspectionPlanNumber',
                    'InspectionPlanVersion',
                    'ProjectNumber',
                    'PriceQuoteNumber',
                    'InspectionPlanCreatedByUserId',
                    'SampleMethodKind',
                    'SampleMethodOther',
                    'SampleMethodRemark',
                    'IntervalDeterminationKind',
                    'NextInspectionInYears',
                    'IntervalDeterminationOther',
                    'IntervalDeterminationRemark',
                    'ShutdownMethodKind',
                    'ShutdownMethodOther',
                    'ShutdownMethodRemark',
                    'PVInstallationTypologyKind',
                    'PVInspectionCoverEntireInstallation',
                    'PVInstallationExludedParts',
                ])
            )
            .nullable()
            .optional(),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
        shutdownMethodKind: z
            .enum([
                'ConformDocumentSuppliedByInstallationAdministrator',
                'CustomerPerformsPowerShutdown',
                'InspectionCompanyIsAuthorized',
                'CustomerDoesNotGivePermission',
                'Other',
            ])
            .nullable()
            .optional(),
        shutdownMethodOther: z.string().max(1000).nullable().optional(),
        shutdownMethodRemark: z.string().max(1000).nullable().optional(),
        pVInstallationTypologyKind: z
            .enum([
                'StringOrCentralInverter',
                'OptimizersWithInverters',
                'MicroInverters',
            ])
            .nullable()
            .optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
    },
    patchElectricalProjectInspectionPlanReportCommand: {
        electricalProjectVersionId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        clearFields: z
            .array(
                z.enum([
                    'InspectionPlanNumber',
                    'InspectionPlanVersion',
                    'ProjectNumber',
                    'PriceQuoteNumber',
                    'InspectionPlanCreatedByUserId',
                    'SampleMethodKind',
                    'SampleMethodOther',
                    'SampleMethodRemark',
                    'IntervalDeterminationKind',
                    'NextInspectionInYears',
                    'IntervalDeterminationOther',
                    'IntervalDeterminationRemark',
                ])
            )
            .nullable()
            .optional(),
        enabled: z.boolean().nullable().optional(),
        inspectionPlanNumber: z.string().max(100).nullable().optional(),
        inspectionPlanVersion: z.string().max(100).nullable().optional(),
        projectNumber: z.string().max(100).nullable().optional(),
        priceQuoteNumber: z.string().max(100).nullable().optional(),
        inspectionPlanCreatedByUserId: z.string().nullable().optional(),
        sampleMethodKind: z
            .enum([
                'ConformNen3140AppendixJOrNTA8220Tabel1',
                'AdditionalInstruction',
                'Complete',
                'LimitToFirstSampleRound',
                'SampleCanBeExpandedBasedOnAppendixJ',
                'TestingAndMeasurementOfNewCablesForLights',
                'ConformNen3140AppendixJ',
                'ConformNTA8220Tabel1',
                'Other',
            ])
            .nullable()
            .optional(),
        sampleMethodOther: z.string().max(1000).nullable().optional(),
        sampleMethodRemark: z.string().max(1000).nullable().optional(),
        intervalDeterminationKind: z
            .enum([
                'ToBeDeterminedDuringInspection',
                'DictatedByInsuranceCompany',
                'NTA8220',
                'Other',
            ])
            .nullable()
            .optional(),
        nextInspectionInYears: z.number().min(0).max(100).nullable().optional(),
        intervalDeterminationOther: z.string().max(1000).nullable().optional(),
        intervalDeterminationRemark: z.string().max(1000).nullable().optional(),
    },
    patchElectricalProjectInspectionScheduleCommand: {
        electricalProjectVersionId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'IsOrderConfirmed',
                    'OrderConfirmationRemark',
                    'StartDate',
                    'EndDate',
                ])
            )
            .nullable()
            .optional(),
        isOrderConfirmed: z.boolean().nullable().optional(),
        orderConfirmationRemark: z.string().max(500).nullable().optional(),
        startDate: z.date().nullable().optional(),
        endDate: z.date().nullable().optional(),
    },
    patchElectricalProjectMeasurementDeviceCommand: {
        electricalProjectMeasurementDeviceId: z.string().min(1),
        addInspectionKind: z
            .enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ])
            .nullable()
            .optional(),
        removeInspectionKind: z
            .enum([
                'None',
                'Scope8',
                'Scope10',
                'Scope12',
                'Nen1010',
                'Nen3140',
            ])
            .nullable()
            .optional(),
    },
    patchElectricalProjectObjectCommand: {
        electricalProjectVersionId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'BagIdentificationNumber',
                    'Name',
                    'Description',
                    'Street',
                    'StreetNumber',
                    'PostalCode',
                    'Locality',
                    'TotalFloorArea',
                    'BuildingConstructionYear',
                    'Email',
                    'TelephoneNumber',
                    'BuildingCount',
                    'DistributionBoardCount',
                    'ControlBoxCount',
                    'IsElectricalInstallationConstructionYearKnown',
                    'ElectricalInstallationConstructionYear',
                    'Remarks',
                    'InspectionPlanCreatorKind',
                    'PVPanelCount',
                    'PVInverterCount',
                    'PVPanelsTotalPower',
                    'PVInvertersTotalPower',
                    'PVBuildKind',
                    'PVInstallationConstructionYear',
                    'PVSpecialSpaceKind',
                    'PVEarthingSystemKind',
                    'PVMainsVoltage',
                ])
            )
            .nullable()
            .optional(),
        bagIdentificationNumber: z.string().max(30).nullable().optional(),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        street: z.string().max(64).nullable().optional(),
        streetNumber: z
            .string()
            .max(32)
            .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
            .nullable()
            .optional(),
        postalCode: z
            .string()
            .max(16)
            .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
            .nullable()
            .optional(),
        locality: z.string().max(64).nullable().optional(),
        totalFloorArea: z.number().min(0).max(1000000).nullable().optional(),
        buildingConstructionYear: z
            .number()
            .min(1000)
            .max(2100)
            .nullable()
            .optional(),
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        buildingCount: z.number().min(0).max(100000).nullable().optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(100000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(100000).nullable().optional(),
        isElectricalInstallationConstructionYearKnown: z
            .boolean()
            .nullable()
            .optional(),
        electricalInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        remarks: z.string().max(1000).nullable().optional(),
        inspectionPlanCreatorKind: z
            .enum([
                'InstallationAdministrator',
                'BuildingOwnerOrAdministrator',
                'Customer',
                'InspectionCompany',
            ])
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(100000).nullable().optional(),
        pVInverterCount: z.number().min(0).max(100000).nullable().optional(),
        pVPanelsTotalPower: z.number().min(0).max(10000).nullable().optional(),
        pVInvertersTotalPower: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pVBuildKind: z
            .enum(['InRoof', 'OnRoof', 'OnField', 'OnWater'])
            .nullable()
            .optional(),
        pVInstallationConstructionYear: z
            .number()
            .min(1800)
            .max(2100)
            .nullable()
            .optional(),
        pVSpecialSpaceKind: z
            .enum(['New', 'Existing', 'Expanded'])
            .nullable()
            .optional(),
        pVEarthingSystemKind: z
            .enum(['TT', 'TN-S', 'TN-C', 'TN-CS', 'IT'])
            .nullable()
            .optional(),
        pVMainsVoltage: z.number().min(0).max(10000).nullable().optional(),
        pVInstallationHasLightningProtection: z.boolean().nullable().optional(),
        pVInspectionCoverEntireInstallation: z.boolean().nullable().optional(),
        pVInstallationExludedParts: z.string().max(1000).nullable().optional(),
        pVAreSpecialSpacesPresent: z.boolean().nullable().optional(),
    },
    patchElectricalProjectObservationCommand: {
        electricalProjectObservationId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'FloorplanElectricalProjectFileId',
                    'FloorplanPinCoordinateX',
                    'FloorplanPinCoordinateY',
                    'LocationRemark',
                    'Remark',
                    'Recommendation',
                    'ElectricalObservationClassificationGroupId',
                    'ElectricalObservationClassificationCodeId',
                    'CodeKind',
                ])
            )
            .nullable()
            .optional(),
        order: z.number().nullable().optional(),
        floorplanElectricalProjectFileId: z.string().nullable().optional(),
        floorplanPinCoordinateX: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        floorplanPinCoordinateY: z
            .number()
            .min(0)
            .max(1000)
            .nullable()
            .optional(),
        locationRemark: z.string().max(100).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
    },
    patchElectricalProjectObservationReportCommand: {
        electricalProjectObservationId: z.string().min(1),
        inspectionKind: z.enum([
            'None',
            'Scope8',
            'Scope10',
            'Scope12',
            'Nen1010',
            'Nen3140',
        ]),
        clearFields: z
            .array(
                z.enum([
                    'Remark',
                    'Recommendation',
                    'ElectricalObservationClassificationGroupId',
                    'ElectricalObservationClassificationCodeId',
                    'CodeKind',
                ])
            )
            .nullable()
            .optional(),
        enabled: z.boolean().nullable().optional(),
        remarkKind: z.enum(['None', 'Same', 'Custom']).nullable().optional(),
        remark: z.string().max(1000).nullable().optional(),
        recommendationKind: z
            .enum(['None', 'Same', 'Custom'])
            .nullable()
            .optional(),
        recommendation: z.string().max(1000).nullable().optional(),
        electricalObservationClassificationGroupId: z
            .string()
            .nullable()
            .optional(),
        electricalObservationClassificationCodeId: z
            .string()
            .nullable()
            .optional(),
        codeKind: z
            .enum(['Severe', 'Serious', 'Minor', 'Remark'])
            .nullable()
            .optional(),
    },
    patchElectricalProjectOrganizationCommand: {
        electricalProjectOrganizationId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'Name',
                    'Description',
                    'Email',
                    'TelephoneNumber',
                    'Website',
                    'Street',
                    'StreetNumber',
                    'PostalCode',
                    'Locality',
                    'AdministrativeArea',
                    'CountryId',
                ])
            )
            .nullable()
            .optional(),
        name: z.string().max(100).nullable().optional(),
        description: z.string().max(250).nullable().optional(),
        patchElectricalProjectOrganizationCommandAddress: {
            street: z.string().max(64).nullable().optional(),
            streetNumber: z
                .string()
                .max(32)
                .regex(/^[1-9]($|[A-Z0-9-]{1,}$)/)
                .nullable()
                .optional(),
            postalCode: z
                .string()
                .max(16)
                .regex(/^[1-9][\d]{3}[A-Za-z]{2}$/)
                .nullable()
                .optional(),
            locality: z.string().max(64).nullable().optional(),
            administrativeArea: z.string().max(64).nullable().optional(),
            countryId: z.string().nullable().optional(),
        },
        email: z
            .union([z.literal(''), z.string().email()])
            .nullable()
            .optional(),
        telephoneNumber: z.string().max(60).nullable().optional(),
        website: z.string().max(250).nullable().optional(),
        isProjectCustomer: z.boolean().nullable().optional(),
        isObjectOwner: z.boolean().nullable().optional(),
        isObjectAdministrator: z.boolean().nullable().optional(),
        isObjectTenant: z.boolean().nullable().optional(),
        isPVInstallationConstructionCompany: z.boolean().nullable().optional(),
    },
    patchElectricalProjectPriceQuoteCommand: {
        electricalProjectVersionId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'OutbuildingCount',
                    'BasePrice',
                    'SquareMeterCount',
                    'PricePerSquareMeter',
                    'DistributionBoardCount',
                    'PricePerDistributionBoard',
                    'ControlBoxCount',
                    'PricePerControlBox',
                    'PricePerRepeatInspection',
                    'PricePerRemoteRepeatInspection',
                    'DiscountPercentage',
                    'PVInverterCount',
                    'PricePerPVInverter',
                    'PVPanelCount',
                    'PricePerPVPanel',
                    'PriceQuoteDate',
                ])
            )
            .nullable()
            .optional(),
        outbuildingCount: z.number().min(0).max(1000).nullable().optional(),
        basePrice: z.number().min(0).max(100000).nullable().optional(),
        squareMeterCount: z.number().min(0).max(10000000).nullable().optional(),
        pricePerSquareMeter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        distributionBoardCount: z
            .number()
            .min(0)
            .max(10000)
            .nullable()
            .optional(),
        pricePerDistributionBoard: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        controlBoxCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerControlBox: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pricePerRemoteRepeatInspection: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        discountPercentage: z.number().min(0).max(25).nullable().optional(),
        pVInverterCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVInverter: z
            .number()
            .min(0)
            .max(10000000)
            .nullable()
            .optional(),
        pVPanelCount: z.number().min(0).max(10000).nullable().optional(),
        pricePerPVPanel: z.number().min(0).max(10000000).nullable().optional(),
        priceQuoteDate: z.date().nullable().optional(),
    },
    patchElectricalProjectPVInverterCommand: {
        electricalProjectPVInverterId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'Identifier',
                    'BrandModel',
                    'SerialNumber',
                    'FirmwareVersion',
                    'RatedPower',
                    'PowerPhaseKind',
                    'ElectricalProjectDistributionBoardId',
                    'GroundImpedanceL1',
                    'GroundImpedanceL2',
                    'GroundImpedanceL3',
                    'NeutralImpedanceL1',
                    'NeutralImpedanceL2',
                    'NeutralImpedanceL3',
                    'ImpedanceL1L2',
                    'ImpedanceL1L3',
                    'ImpedanceL2L3',
                ])
            )
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        internalRemark: z.string().max(500).nullable().optional(),
        internalImageRemark: z.string().max(500).nullable().optional(),
        internalThermalImageRemark: z.string().max(500).nullable().optional(),
        brandModel: z.string().max(100).nullable().optional(),
        serialNumber: z.string().max(100).nullable().optional(),
        firmwareVersion: z.string().max(100).nullable().optional(),
        ratedPower: z.number().min(0).max(100000).nullable().optional(),
        powerPhaseKind: z.enum(['1', '3']).nullable().optional(),
        order: z.number().nullable().optional(),
        electricalProjectDistributionBoardId: z.string().nullable().optional(),
        groundImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        groundImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL1: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        neutralImpedanceL3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L2: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL1L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        impedanceL2L3: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        reportImages: z.boolean().nullable().optional(),
        reportThermalImages: z.boolean().nullable().optional(),
        reportImpedanceMeasurements: z.boolean().nullable().optional(),
        reportPVStrings: z.boolean().nullable().optional(),
    },
    patchElectricalProjectPVStringCommand: {
        electricalProjectPVStringId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'Identifier',
                    'Voltage',
                    'Resistance',
                    'Current',
                    'Order',
                ])
            )
            .nullable()
            .optional(),
        identifier: z.string().max(100).nullable().optional(),
        voltage: z.number().min(0).max(100000).nullable().optional(),
        resistance: z.number().min(0).max(100000).nullable().optional(),
        current: z.number().min(0).max(100000).nullable().optional(),
        order: z.number().nullable().optional(),
    },
    patchElectricalProjectRisoMeasurementCommand: {
        electricalProjectRisoMeasurementId: z.string().min(1),
        clearFields: z
            .array(z.enum(['CircuitKind', 'MeasuredValue', 'MinimumValue']))
            .nullable()
            .optional(),
        circuitKind: z
            .enum([
                'L-PE / N-PE',
                'L1-L2',
                'L2-L3',
                'L1-L3',
                'L1-N',
                'L2-N',
                'L3-N',
                'L1-PE',
                'L2-PE',
                'L3-PE',
                'N-PE',
                'L-PE',
            ])
            .nullable()
            .optional(),
        measuredValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        minimumValue: z
            .number()
            .min(-1000000000)
            .max(1000000000)
            .nullable()
            .optional(),
        isFaulty: z.boolean().nullable().optional(),
        order: z.number().nullable().optional(),
    },
    patchElectricalProjectSelectListItemCommand: {
        electricalProjectVersionId: z.string().min(1),
        listKind: z.enum([
            'InspectionExclusion',
            'ObjectSpecialSpace',
            'AdditionalInstruction',
            'AdditionalTopic',
            'InspectionComponent',
            'BuildingFunction',
        ]),
        patchElectricalProjectSelectListItemCommandItem: {
            electricalProjectSelectListItemId: z.string().min(1),
            selected: z.boolean().nullable().optional(),
            addInspectionKind: z
                .enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
                .nullable()
                .optional(),
            removeInspectionKind: z
                .enum([
                    'None',
                    'Scope8',
                    'Scope10',
                    'Scope12',
                    'Nen1010',
                    'Nen3140',
                ])
                .nullable()
                .optional(),
            customValue: z.string().max(500).nullable().optional(),
        },
    },
    patchElectricalProjectTechnicalDrawingCommand: {
        electricalProjectTechnicalDrawingId: z.string().min(1),
        clearFields: z
            .array(
                z.enum([
                    'IsDrawingPresent',
                    'ElectricalProjectFileId',
                    'Location',
                    'Description',
                ])
            )
            .nullable()
            .optional(),
        isDrawingPresent: z.boolean().nullable().optional(),
        electricalProjectFileId: z.string().nullable().optional(),
        name: z.string().max(200).nullable().optional(),
        location: z.string().max(200).nullable().optional(),
        description: z.string().max(500).nullable().optional(),
        order: z.number().nullable().optional(),
    },
    publishElectricalProjectCommand: {
        electricalProjectVersionId: z.string().min(1),
    },
    removeRoleFromUserCommand: {
        userId: z.string().min(1),
    },
    repairElectricalProjectObservationPortalCommand: {
        electricalProjectObservationId: z.string().min(1),
        isFixed: z.boolean(),
    },
    resetAccountPasswordV2Request: {
        userId: z.string().min(1),
        resetCode: z.string().min(1).max(1024),
        password: z
            .string()
            .min(10)
            .max(256)
            .regex(/^.{10,256}$/),
    },
    revokeElectricalProjectCommand: {
        electricalProjectVersionId: z.string().min(1),
    },
} as const;
